import type { AttentionItem, HealthState, Overview as OverviewData } from '@central/api';
import {
  Card,
  KpiTile,
  PageHeader,
  StatusChip,
  color,
  formatAgo,
  formatDate,
  status,
} from '@central/ui';
import { Button, Group, TextInput } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { EnvironmentsTable, HealthFilter, VersionFilter } from '../components/EnvironmentsTable';
import { useEnvironmentPages } from '../lib/environment';
import { Empty, LoadMore, QueryState } from '../components/parts';
import { muted } from '../lib/labels';
import { useCanWrite } from '../lib/me';
import { formatIn } from '../lib/format';
import { shortKey } from '../lib/environment';

const attentionLabel: Record<AttentionItem['kind'], string> = {
  environment_down: 'Down',
  environment_silent: 'Silent',
  environment_degraded: 'Degraded',
  pending_approval: 'Pending approval',
  seats_near_limit: 'Near limit',
  environment_unknown: 'Unknown',
  license_expiring: 'Expiring',
};

/** How often the overview and its environment table refresh. */
const refreshEvery = 30_000;

/** Overview (docs/design/AdminOverview.dc.html). */
export function Overview() {
  const api = useApi();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const [search, setSearch] = useState('');
  const [health, setHealth] = useState<HealthState>();
  const [version, setVersion] = useState<string>();
  const overview = useQuery({
    queryKey: keys.overview,
    queryFn: () => api.overview(),
    refetchInterval: refreshEvery,
  });
  const envs = useEnvironmentPages({ health, version }, { refetchInterval: refreshEvery });

  const onSearch = (e: FormEvent) => {
    e.preventDefault();
    void navigate({ to: '/customers', search: { q: search.trim() || undefined } });
  };

  return (
    <>
      <PageHeader
        title="Overview"
        subtitle={
          <>
            All customers
            {overview.dataUpdatedAt > 0 && (
              <>
                {' · '}
                <UpdatedAgo at={overview.dataUpdatedAt} />
              </>
            )}
          </>
        }
        actions={
          <>
            <form onSubmit={onSearch} role="search">
              <TextInput
                aria-label="Search customers"
                placeholder="Search customers, keys, environments…"
                size="sm"
                h={40}
                w={300}
                maw="100%"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
              />
            </form>
            {canWrite && (
              <Button
                color="dark"
                size="sm"
                h={40}
                onClick={() => void navigate({ to: '/customers', search: { new: true } })}
              >
                New customer
              </Button>
            )}
          </>
        }
      />
      <QueryState isPending={overview.isPending} error={overview.error}>
        {() => <OverviewBody data={overview.data!} />}
      </QueryState>
      <Card
        title="All environments"
        padded={false}
        action={
          <Group gap={8} wrap="wrap" justify="flex-end">
            <HealthFilter
              value={health}
              onChange={(v) => setHealth(v as HealthState | undefined)}
            />
            <VersionFilter
              value={version}
              versions={overview.data?.versions ?? []}
              onChange={setVersion}
            />
          </Group>
        }
      >
        <QueryState isPending={envs.isPending} error={envs.error}>
          {() =>
            envs.items.length === 0 ? (
              <Empty>{health || version ? 'No environments match.' : 'No environments yet.'}</Empty>
            ) : (
              <EnvironmentsTable items={envs.items} label="All environments" variant="overview" />
            )
          }
        </QueryState>
        {envs.hasNextPage && (
          <LoadMore
            what="environments"
            loading={envs.isFetchingNextPage}
            onClick={() => void envs.fetchNextPage()}
          />
        )}
      </Card>
    </>
  );
}

/** "updated 12 s ago", ticking while the page is open. */
function UpdatedAgo({ at }: { at: number }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 5_000);
    return () => clearInterval(id);
  }, []);
  return (
    <>updated {formatAgo(new Date(at).toISOString(), new Date(Math.max(now.getTime(), at)))}</>
  );
}

function OverviewBody({ data }: { data: OverviewData }) {
  const k = data.kpis;
  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <KpiTile label="Customers" value={k.customers} />
        <KpiTile label="Environments" value={k.environments} />
        <KpiTile label="Healthy" value={k.healthy} valueColor={status.ok.fg} />
        <KpiTile
          label="Degraded · Down"
          value={
            <>
              <span style={{ color: status.warn.fg }}>{k.degraded}</span> ·{' '}
              <span style={{ color: status.down.fg }}>{k.down}</span>
            </>
          }
        />
        <KpiTile label="Licenses expiring ≤ 60 d" value={k.licensesExpiring60d} />
        <KpiTile label="Maintenance lapsing ≤ 60 d" value={k.maintenanceLapsing60d} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <Card
          title="Needs attention"
          padded={false}
          style={{ flex: '3 1 420px' }}
          action={
            <Link to="/environments" search={{ attention: true }} style={{ fontSize: 13 }}>
              All alerts
            </Link>
          }
        >
          {data.attention.length === 0 ? (
            <Empty>Nothing needs attention.</Empty>
          ) : (
            <ul aria-label="Needs attention" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {data.attention.map((a, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 14,
                    alignItems: 'center',
                    padding: '12px 20px',
                    borderTop: `1px solid ${color.rowRule}`,
                  }}
                >
                  <StatusChip tone={a.tone}>{attentionLabel[a.kind]}</StatusChip>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{a.title}</span>
                    <span style={{ fontSize: 13, ...muted }}>{a.detail}</span>
                  </div>
                  <AttentionLink item={a} />
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Renewals coming up" padded={false} style={{ flex: '2 1 320px' }}>
          {data.renewals.length === 0 ? (
            <Empty>No renewals in the next 60 days.</Empty>
          ) : (
            <ul
              aria-label="Renewals coming up"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {data.renewals.map((r) => {
                const due = soonest(r.licenseKey, r.expiresAt, r.maintenanceEndsAt);
                return (
                  <li
                    key={r.licenseId}
                    style={{
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      padding: '12px 20px',
                      borderTop: `1px solid ${color.rowRule}`,
                      fontSize: 14,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <Link
                        to="/customers/$customerId"
                        params={{ customerId: r.customerId }}
                        style={{ fontWeight: 600 }}
                      >
                        {r.customerName}
                      </Link>
                      <Link
                        to="/licenses/$licenseId"
                        params={{ licenseId: r.licenseId }}
                        style={{ fontSize: 13 }}
                      >
                        <span title={due.key}>{due.what}</span>
                      </Link>
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
                      <span>{formatDate(due.at)}</span>
                      <span style={{ fontSize: 13, ...muted }}>{formatIn(due.at)}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

/** The renewal that comes first: the license expiry or the maintenance end. */
function soonest(licenseKey: string, expiresAt: string, maintenanceEndsAt?: string) {
  if (maintenanceEndsAt && maintenanceEndsAt < expiresAt)
    return { what: 'Maintenance plan', at: maintenanceEndsAt, key: undefined };
  return { what: `License ${shortKey(licenseKey)}`, at: expiresAt, key: licenseKey };
}

function AttentionLink({ item }: { item: AttentionItem }) {
  const style = { fontSize: 13, whiteSpace: 'nowrap' as const };
  if (item.environmentId)
    return (
      <Link
        to="/environments/$environmentId"
        params={{ environmentId: item.environmentId }}
        style={style}
      >
        Open
      </Link>
    );
  if (item.licenseId)
    return (
      <Link to="/licenses/$licenseId" params={{ licenseId: item.licenseId }} style={style}>
        Open
      </Link>
    );
  if (item.customerId)
    return (
      <Link to="/customers/$customerId" params={{ customerId: item.customerId }} style={style}>
        Open
      </Link>
    );
  return null;
}
